import React, { useEffect, useState, useRef } from 'react';
import { ChevronLeft } from 'lucide-react';
import ComercialDropDown from '../obras/inputs/NewComercialDropDown';
import EstadoObraDropdown from '../obras/inputs/EstadoObraDropdown';
import TipoMaterialDropdown from '../obras/inputs/TipoMaterialDropdown';
// import EstadoOfertaDropdown from '../obras/inputs/EstadoOfertaDropdown';
import ConstructoraDropdown from '../obras/inputs/NewConstructoraDropdown';
import SegmentoDropdown from '../obras/inputs/SegmentoDropdown';
import CiudadDropDown from '../obras/inputs/CiudadDropDown01';
import ZonaDropDown from '../obras/inputs/ZonaDropDown';
import DelegacionDropDown from '../obras/inputs/DelegacionDropDown';
import ClienteDropdown from '../obras/inputs/ClienteDropdown';
import ClienteDropdownSubcontrata from '../obras/inputs/ClienteDropdownSubcontrata';
import TipoObraDropdown from '../obras/inputs/TipoObraDropdown';
import MapaUbicacion from '../obras/inputs/MapaUbicacion';
import axios from 'axios';
import { getApiUrl } from '../../utils/apiConfig';
import { usePermisos } from '../../context/PermisosContext';
import AdminReadOnlyAlert from '../AdminReadOnlyAlert';

import { CircleCheck, CircleX } from 'lucide-react';

const formatCurrency = (value) => {
  if (value === '' || value === null || value === undefined || isNaN(value)) return '';
  const formatted = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
  }).format(value);

  return formatted.replace('€', '€ ');
};

const todayLocal = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
};

const emptySubcontrata = () => ({
  IdCliente: null,
  nombre: '',
  especialidad: '',
  contacto: { nombre: '', telefono: '', email: '' },
});


const ObraNueva = ({ obra, setObra, onSave, onCancel, user, comercialIdsPermitidos, ubicacionesPermitidas, infoComerciales, refreshObras, idsSeccionesPermitidos }) => {
  const { isAdmin } = usePermisos(); // 🔒 Obtener si es admin
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [originalObra, setOriginalObra] = useState(null);

  const [errorMsg, setErrorMsg] = useState('');
  const [subcontratas, setSubcontratas] = useState([emptySubcontrata()]);
  const [emailError, setEmailError] = useState('');
  const [subcontrataEmailErrors, setSubcontrataEmailErrors] = useState({});
  const [tiposObra, setTiposObra] = useState([]);

  // Cargar tipos de obra para validación
  useEffect(() => {
    const loadTipos = async () => {
      try {
        const res = await axios.get(`${getApiUrl('/api/tiposObra')}`);
        setTiposObra(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        console.error("Error cargando tipos de obra:", e);
        setTiposObra([]);
      }
    };
    loadTipos();
  }, []);

  const isModified = originalObra !== JSON.stringify(obra);

  // console.log('COMERCIALES PERMITIDOS OBRA NUEVA ' + comercialIdsPermitidos)

  if (!obra || typeof obra !== 'object' || Object.keys(obra).length === 0) return null;

  // Validación de campos obligatorios (solo los realmente obligatorios en la BD)
  const camposObligatorios = [
    'IdComercial',
    'IdUbicacion',
    'IdConstructora',
    'Obra',
    'FechaVisita',
    'EstadoObra',
    'NombreCV',
    'TelefonoCV',
    'EmailCV',
    // 'NumeroDireccion',
    'TipoMaterial',
    'TipoObraNombre',
    // TipoObraInfoExtra es condicional, se valida aparte
  ];

  const isFormValid = camposObligatorios.every(campo => {
    const valor = obra[campo];
    return valor !== undefined && valor !== null && String(valor).trim() !== '';
  });

  const addSubcontrata = () => {
    setSubcontratas(prev => {
      if (prev.length >= 5) return prev;
      return [
        ...prev,
        {
          nombre: '',
          especialidad: '',
          contacto: { nombre: '', telefono: '', email: '' }
        }
      ];
    });
  };

  const updateSubcontrata = (index, path, value) => {
    setSubcontratas(prev =>
      prev.map((sc, i) => {
        if (i !== index) return sc;
        if (path.startsWith('contacto.')) {
          const campo = path.split('.')[1];
          return { ...sc, contacto: { ...sc.contacto, [campo]: value } };
        }
        return { ...sc, [path]: value };
      })
    );
  };

  const removeSubcontrata = (index) => {
    setSubcontratas(prev => {
      if (prev.length <= 1) {
        const copia = [...prev];
        copia[0] = emptySubcontrata();
        return copia;
      }
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleSave = async () => {
    // console.log('Obra a guardar:', obra);
    
    // Validar email principal antes de continuar
    if (emailError) {
      setErrorMsg('Por favor, corrige el email del contacto visitado antes de guardar');
      setTimeout(() => setErrorMsg(''), 5000);
      return;
    }
    
    // Validar emails de subcontratas
    if (Object.keys(subcontrataEmailErrors).length > 0) {
      setErrorMsg('Por favor, corrige los emails de las subcontratas antes de guardar');
      setTimeout(() => setErrorMsg(''), 5000);
      return;
    }
    
    // Validar campos obligatorios ANTES de intentar guardar
    const camposFaltantes = camposObligatorios.filter(campo => {
      const valor = obra[campo];
      return !valor || String(valor).trim() === '';
    });

    // Validación condicional de TipoObraInfoExtra basada en el tipo seleccionado
    const tipoSeleccionado = tiposObra.find(t => t.IdTipoObra === obra.IdTipoObra);
    if (tipoSeleccionado) {
      const nombre = (tipoSeleccionado.Nombre || '').trim().toUpperCase();
      const extraEtiqueta = tipoSeleccionado.ExtraEtiqueta?.trim();
      
      // Requiere campo extra si es: Obra Pública, Otros, Reforma Integral, o tiene ExtraEtiqueta
      const requiereInfoExtra = 
        nombre === 'OBRA PÚBLICA' ||
        nombre === 'OTROS' ||
        nombre === 'REFORMA INTEGRAL' ||
        Boolean(extraEtiqueta);
      
      if (requiereInfoExtra) {
        const infoExtra = obra.TipoObraInfoExtra || '';
        if (!infoExtra || String(infoExtra).trim() === '') {
          camposFaltantes.push('TipoObraInfoExtra');
        }
      }
    }

    if (camposFaltantes.length > 0) {
      const nombresLegibles = {
        'IdComercial': 'Comercial',
        'IdUbicacion': 'Ubicación (Población y Zona)',
        'IdConstructora': 'Constructora',
        'Obra': 'Nombre de la Obra',
        'FechaVisita': 'Fecha de Visita',
        'EstadoObra': 'Estado de Obra',
        'NombreCV': 'Nombre del Contacto',
        'TelefonoCV': 'Teléfono del Contacto',
        'EmailCV': 'Email del Contacto',
        // 'NumeroDireccion': 'Número de la Obra',
        'TipoMaterial': 'Tipo de Material',
        'TipoObraNombre': 'Tipo de Obra',
        'TipoObraInfoExtra': 'Información adicional del Tipo de Obra',
      };
      
      const camposLegibles = camposFaltantes.map(c => nombresLegibles[c] || c);
      
      // Usar un separador especial para distinguir el título de la lista
      const listaCampos = camposLegibles.map(c => `• ${c}`).join('\n');
      
      setErrorMsg(`TITULO||Faltan campos obligatorios:||LISTA||${listaCampos}`);
      
      // Auto-ocultar después de 5 segundos
      setTimeout(() => setErrorMsg(''), 5000);
      return;
    }
    
    setSaving(true);
    setSuccessMsg('');
    setErrorMsg('');
    
    // Mapear los campos del frontend a los del backend
    const payload = {
      IdComercial: obra.IdComercial,
      IdConstructora: obra.IdConstructora,
      JefeObra: obra.JefeObra || null,
      TelefonoC: obra.TelefonoC || null,
      ObservacionesC: obra.ObservacionesC || null,
      IdUbicacion: obra.IdUbicacion,
      IdCliente: obra.IdCliente || null,
      IdSegmento: obra.IdSegmento || null,
      // ✅ Corregido: usar los campos correctos del formulario
      NombreCV: obra.NombreCV || null,
      TelefonoCV: obra.TelefonoCV || null,
      EmailCV: obra.EmailCV || null,
      ObservacionesCV: obra.ObservacionesCV || null,
      NumeroDireccion: obra.NumeroDireccion || null,
      Obra: obra.Obra,
      Ofertado: obra.Ofertado || null,
      TecnicoComercial: obra.TecnicoComercial || null,
      FechaVisita: obra.FechaVisita,
      IdTipoObra: obra.IdTipoObra || null,
      TipoObraInfoExtra: obra.TipoObraInfoExtra || null,
      EstadoObra: obra.EstadoObra,
      // EstadoOferta: obra.EstadoOferta || null,
      NOferta: obra.NOferta || null,
      Otros: obra.Otros || null,
      Direccion: obra.Direccion || null,
      LatLong: obra.LatLong || null,
      TipoMaterial: obra.TipoMaterial || null
    };

    

    try {
      // 1. Crear la obra
      const res = await fetch(`${getApiUrl('/api/nuevaObra')}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.message || 'Error al crear la obra');
      }
      
      const resp = await res.json();
      const idObraCreada = resp.IdObra;
      
      // 2. Crear las subcontratas si hay datos válidos
      const subcontratatasValidas = subcontratas.filter(sc => 
        sc.nombre?.trim() &&
        sc.especialidad?.trim()
      );

      if (subcontratatasValidas.length > 0 && idObraCreada) {
        try {
          // Mapear al formato del backend
          const subcontratatasPayload = subcontratatasValidas.map(sc => ({
            IdCliente: sc.IdCliente || null,
            Nombre: sc.nombre,
            Especialidad: sc.especialidad,
            NombreC: sc.contacto.nombre,
            TelefonoC: sc.contacto.telefono,
            EmailC: sc.contacto.email,
            Comentarios: obra.Comentarios || null
          }));

      const subcontratatasRes = await fetch(getApiUrl('/api/subcontratas'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              IdObra: idObraCreada,
              subcontratas: subcontratatasPayload
            })
          });

          if (!subcontratatasRes.ok) {
            console.warn('No se pudieron crear todas las subcontratas');
          }
        } catch (subErr) {
          console.error('Error al crear subcontratas:', subErr);
          // No bloqueamos el flujo si fallan las subcontratas
        }
      }
      
      setSuccessMsg(resp.message || '¡Obra creada correctamente!');
      setSaving(false);
      
      // Esperar 3 segundos para que el usuario vea el mensaje
      setTimeout(async () => {
        if (typeof refreshObras === 'function') {
          await refreshObras();
        }
        onCancel(); // Cerrar DESPUÉS de mostrar el mensaje
      }, 3000);
      
    } catch (err) {
      console.error('Error al guardar obra:', err);
      setErrorMsg(err.message || 'Error al crear la obra');
      setSaving(false);
      
      // Auto-ocultar después de 5 segundos
      setTimeout(() => setErrorMsg(''), 5000);
    }
  };

  return (
    // <div className="flex flex-col gap-4 w-full max-w-full no-select">
    <div className="p-4 sm:p-6 lg:p-6 bg-gray-50 min-h-screen no-select">
      <div className="max-w-7xl mx-auto">

      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 my-4 sm:gap-0 sm:justify-between sm:items-center">
        <div className="flex items-center gap-6">
          {/* Botón volver */}
          <button
            onClick={onCancel}
            className="flex items-center justify-center w-10 h-10 rounded-lg bg-white border border-gray-300 hover:bg-gray-50 hover:border-gray-400 transition-colors"
            title="Volver a Obras"
          >
            <ChevronLeft className="w-6 h-6 text-gray-600" />
          </button>
          
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">Obras</h1>
            <p className="text-gray-600 text-sm sm:text-base">Crear nueva obra</p>
          </div>
        </div>
        
        <div className="flex items-center gap-3 bg-white px-3 sm:px-4 py-2 sm:py-3 rounded-xl shadow-sm border hover:shadow-lg hover:border-blue-200 transition-all duration-300 cursor-pointer">
          <div className="w-7 h-7 sm:w-8 sm:h-8 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full flex items-center justify-center text-white font-semibold text-xs sm:text-sm">
            {user?.name?.charAt(0)?.toUpperCase() || 'U'}
          </div>
          <div className="text-right">
            <p className="font-medium text-gray-800 text-xs sm:text-sm md:text-base truncate max-w-[100px] sm:max-w-[150px] md:max-w-none">
              {user?.name || 'Usuario'}
            </p>
          </div>
        </div>
      </div>


      {/* Formulario */}
      <div className="w-full">

        {/* 🔒 Alerta para admins */}
        {isAdmin && <AdminReadOnlyAlert tipo="obra" />}

        {/* <div className='grid grid-cols-1 md:grid-cols-2 gap-6 mb-6'> */}

        {/* COMERCIAL */}
        <div className=" flex flex-col  gap-3 border rounded-lg bg-white p-4 shadow-sm sm:p-4 w-full mb-6">
          <p className="font-medium">Información Comercial</p>

          {/* Campos 1 */}
          <div className="flex flex-col sm:flex-row gap-4 w-full">
            <div className="flex-1 min-w-0 sm:min-w-[180px] max-w-full sm:max-w-[200px]">
              <label className="block mb-1 text-xs font-medium text-gray-700 truncate">Fecha visita*</label>
              <input
                type="date"
                id="FechaVisita"
                className="inputModal truncate w-full p-2 border rounded bg-white mb-0"
                value={obra.FechaVisita?.slice(0, 10) || todayLocal()}
                onChange={(e) => setObra({ ...obra, FechaVisita: e.target.value })}
              />
            </div>
            <div className="flex-1 min-w-0 sm:min-w-[180px] max-w-full">
              <ComercialDropDown selectedObra={obra} setSelectedObra={setObra} infoComerciales={infoComerciales} comercialIdsPermitidos={comercialIdsPermitidos} />
            </div>
            <div className="flex-1 min-w-0 sm:min-w-[180px] max-w-full">
              <label className="block mb-1 text-xs font-medium text-gray-700 truncate">Nombre Técnico Comercial (BME)*</label>
              <input type="text" className="truncate inputModal shadow-sm w-full p-2 border rounded bg-white mb-0" value={obra.TecnicoComercial || ''} onChange={e => setObra({ ...obra, TecnicoComercial: e.target.value })} />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 w-full">
            {/* <div className="flex-1 min-w-[180px]">
              <label className="block mb-1 text-xs font-medium text-gray-700 truncate">Delegación*</label>
              <input type="text" className="truncate inputModal shadow-sm w-full p-2 border rounded bg-white mb-0" value={obra.Delegacion || ''} onChange={e => setObra({ ...obra, Delegacion: e.target.value })} />
            </div> */}

            <div className="flex-1 min-w-[180px]">
              <DelegacionDropDown selectedObra={obra} setSelectedObra={setObra} idsSeccionesPermitidos={idsSeccionesPermitidos}/>
            </div>


            <CiudadDropDown
              required
              poblacionSeleccionada={obra.Poblacion}
              selectedObra={obra}
              setSelectedObra={setObra}
              ubicacionesPermitidas={ubicacionesPermitidas}
            />

            <div className="flex-1 min-w-[180px]">
              <ZonaDropDown
                required
                poblacionSeleccionada={obra.Poblacion}
                selectedObra={obra}
                setSelectedObra={setObra}
                ubicacionesPermitidas={ubicacionesPermitidas}
              />
            </div>



            {/* <div className="flex-1 min-w-[180px]">
              <ConstructoraDropdown selectedObra={obra} setSelectedObra={setObra}/>
            </div>
            <div className="flex-1 min-w-[180px]">
              <label className="block mb-1 text-sm font-medium text-gray-700 truncate">Jefe de obra*</label>
              <input type="text" className="truncate inputModal shadow-sm w-full p-2 border rounded bg-white mb-0" value={obra.JefeObra || ''} onChange={e => setObra({ ...obra, JefeObra: e.target.value })} />
            </div> */}



          </div>

          {/* <div className="flex flex-col sm:flex-row gap-4 w-full">
            <div className="flex-1 min-w-[180px]">
              <label className="block mb-1 text-sm font-medium text-gray-700 truncate">Teléfono Comercial*</label>
              <input 
                required
                type="tel" 
                inputMode="numeric"
                pattern="[0-9]{9,15}"
                maxLength={9}
                minLength={9}
                className="inputModal shadow-sm w-full p-2 border rounded bg-white mb-0 truncate"
                value={obra.TelefonoC || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^\d*$/.test(value)) {
                    setObra({ ...obra, TelefonoC: value });
                  }
                }}
              />
            </div>
            <div className="flex-1 min-w-[180px]">
              <CiudadDropDown selectedObra={obra} setSelectedObra={setObra} ubicacionesPermitidas={ubicacionesPermitidas}/>
            </div>
            <div className="flex-1 min-w-[180px]">
              <ZonaDropDown
                required
                poblacionSeleccionada={obra.Poblacion}
                selectedObra={obra}
                setSelectedObra={setObra}
                ubicacionesPermitidas={ubicacionesPermitidas}
              />
            </div>
          </div> */}

          <div className="flex flex-col w-full">
            <div className="flex-1 min-w-[180px]">
              <label className="block mb-1 text-xs font-medium text-gray-700 truncate">Observaciones Comercial</label>
              <textarea className="truncatecomentarios shadow-sm w-full p-2 border rounded bg-white mb-0 resize-y min-h-[60px] text-sm" value={obra.ObservacionesC || ''} onChange={e => setObra({ ...obra, ObservacionesC: e.target.value })} />
            </div>
          </div>
        </div>


        {/* <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 w-full mt-6"> */}

        {/* INSTALADOR*/}
        <div className="flex flex-col gap-4 border rounded-lg bg-white p-4 shadow-sm sm:p-4 w-full">
          
          <p className="font-medium">Información de la Obra</p>
          
          {/* Fila 1: Constructora + Nombre Obra */}
          <div className="flex flex-col sm:flex-row gap-3 w-full">
            <div className="flex-1 min-w-[180px]">
              <ConstructoraDropdown selectedObra={obra} setSelectedObra={setObra}/>
            </div>
            <div className="flex-1 min-w-[180px]">
              <label className="block mb-1 text-xs font-medium text-gray-700 truncate">Nombre Obra*</label>
              <input type="text" className="truncate inputModal shadow-sm w-full p-2 border rounded bg-white mb-0" value={obra.Obra || ''} onChange={e => setObra({ ...obra, Obra: e.target.value })} />
            </div>
          </div>

          <hr />

          {/* Fila 2: Estado Obra + Tipo Material + Tipo Obra */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 w-full">
            <div className="min-w-[180px]">
              <EstadoObraDropdown selectedObra={obra} setSelectedObra={setObra} />
            </div>
            <div className="min-w-[180px]">
              <TipoMaterialDropdown selectedObra={obra} setSelectedObra={setObra} />
            </div>
            <div className="min-w-[180px]">
              <TipoObraDropdown selectedObra={obra} setSelectedObra={setObra} />
            </div>
          </div>

          <hr />

          {/* Grid con 3 columnas: Nombre + Teléfono + Mapa */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 w-full">
            {/* Columna 1 y 2: Nombre y Teléfono arriba, Email abajo */}

              {/* Fila 1: Nombre + Teléfono */}
              <div className="min-w-[180px]">
                <label className="block mb-1 text-xs font-medium text-gray-700 truncate">Nombre*</label>
                <input type="text" className="truncate inputModal shadow-sm w-full p-2 border rounded bg-white mb-0" value={obra.NombreCV || ''} onChange={e => setObra({ ...obra, NombreCV: e.target.value })} />
              </div>
              <div className="min-w-[180px]">
                <label className="block mb-1 text-xs font-medium text-gray-700 truncate">Teléfono*</label>
                <input 
                  type="tel" 
                  inputMode="numeric"
                  pattern="[0-9]{9,15}"
                  maxLength={9}
                  minLength={9}
                  className="inputModal shadow-sm w-full p-2 border rounded bg-white mb-0 truncate"
                  value={obra.TelefonoCV || ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^\d*$/.test(value)) {
                      setObra({ ...obra, TelefonoCV: value });
                    }
                  }}
                />
              </div>
              
              {/* Fila 2: Email (ocupa ambas columnas) */}
              <div className="min-w-[180px]">
                <label className="block mb-1 text-xs font-medium text-gray-700 truncate">Email*</label>
                <input
                  type="email"
                  id="Email"
                  name="Email"
                  inputMode="email"
                  autoComplete="email"
                  spellCheck={false}
                  required
                  className={`inputModal shadow-sm w-full p-2 border rounded bg-white mb-0 truncate ${
                    emailError ? 'border-red-500 focus:ring-red-500' : ''
                  }`}
                  placeholder="nombre@dominio.com"
                  value={obra.EmailCV || ''}
                  onChange={(e) => {
                    const v = e.target.value.replace(/^\s+/, '');
                    setObra({ ...obra, EmailCV: v });
                    
                    if (v && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
                      setEmailError('Email inválido');
                    } else {
                      setEmailError('');
                    }
                  }}
                  onBlur={(e) => {
                    const v = e.target.value.trim().toLowerCase();
                    setObra({ ...obra, EmailCV: v });
                    
                    if (v) {
                      const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
                      if (!emailRegex.test(v)) {
                        setEmailError('Formato de email inválido. Debe incluir @ y un dominio válido (.com, .es, etc.)');
                      } else {
                        setEmailError('');
                      }
                    } else {
                      setEmailError('');
                    }
                  }}
                />
                {emailError && (
                  <p className="text-red-500 text-xs mt-1">{emailError}</p>
                )}
            </div>

          </div>

          
          {/* Layout 2 columnas */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 w-full">
            <div className="lg:col-span-2 flex flex-col gap-4">
              
              {/* Número y Ubicación - En móvil uno debajo del otro, desde md en la misma fila */}
              <div className="flex flex-col md:flex-row gap-3">
                <div className="w-full md:w-32">
                  <label className="block mb-1 text-xs font-medium text-gray-700 truncate">Número</label>
                  <input
                    type="text"
                    inputMode="text"
                    maxLength={6}
                    minLength={1}
                    className="inputModal shadow-sm w-full p-2 border rounded bg-white mb-0 truncate"
                    value={obra.NumeroDireccionInput || obra.NumeroDireccion || ''}
                    onChange={(e) => {
                      let value = e.target.value;
                      // Permitir escribir cualquier cosa, pero solo guardar si es válido
                      setObra({
                        ...obra,
                        NumeroDireccionInput: value
                      });
                      if (/^\d+$/.test(value)) {
                        setObra(prev => ({ ...prev, NumeroDireccion: value, NumeroDireccionInput: value }));
                      } else if (/^s\/?n$/i.test(value)) {
                        setObra(prev => ({ ...prev, NumeroDireccion: value.toUpperCase(), NumeroDireccionInput: value.toUpperCase() }));
                      } else if (value === '') {
                        setObra(prev => ({ ...prev, NumeroDireccion: '', NumeroDireccionInput: '' }));
                      } else {
                        setObra(prev => ({ ...prev, NumeroDireccion: '', NumeroDireccionInput: value }));
                      }
                    }}
                  />
                </div>

                <div className='flex-1 min-w-[180px]'>
                  <label className="block mb-1 text-xs font-medium text-gray-700 truncate">
                    Ubicación / Dirección
                  </label>
                  <input
                    type="text"
                    placeholder="Ej: Calle Mayor 123, Madrid"
                    className="inputModal w-full shadow-sm p-2 border rounded bg-white truncate"
                    value={obra.Direccion || ''}
                    onChange={(e) => setObra({ ...obra, Direccion: e.target.value })}
                  />
                </div>
              </div>

              <div className="w-full">

                <MapaUbicacion obra={obra} setObra={setObra} />

              </div>

            </div>

            <div className="lg:col-span-1 flex flex-col">
              <label
                  htmlFor="comentarios-obra"
                  className="block mb-1 text-xs font-medium text-gray-700 truncate"
                >
                  Comentarios Obra
              </label>
              <textarea
                id="comentarios-obra"
                className="truncatecomentarios shadow-sm w-full p-2 border rounded bg-white mb-0 resize-y min-h-[60px] text-sm flex-1"
                value={obra.ObservacionesCV || ''}
                onChange={e => setObra({ ...obra, ObservacionesCV: e.target.value })}
                placeholder="Añade observaciones, requisitos, etc."
              />

            </div>
          </div>


        </div>


    


        {/* INFORMACIÓN SUBCONTRATAS */}
        {/* Encabezado */}
        <div className="flex items-center mb-2 mt-6">
          <p className="font-medium">Información de las Subcontratas</p>
        </div>
    
        {/* GRID: los formularios + la tarjeta "Añadir" como un item más */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {subcontratas.map((sc, idx) => {
            // Validar si la subcontrata está completa (solo nombre y especialidad obligatorios)
            const isSubcontrataComplete = 
              sc.nombre?.trim() &&
              sc.especialidad?.trim();

            return (
            <div
              key={idx}
              className="border rounded-lg bg-white p-4 shadow-sm flex flex-col gap-4"
            >
              <div className="flex justify-between items-center gap-2">
                <h4 className="font-semibold text-sm text-gray-700">
                  Subcontrata {idx + 1}
                </h4>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => removeSubcontrata(idx)}
                    disabled={subcontratas.length === 1}
                    className={`p-2 rounded-xl ${
                      subcontratas.length === 1
                        ? 'bg-gray-100 text-gray-300 cursor-not-allowed'
                        : 'bg-red-50 text-red-600 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-200'
                    }`}
                    aria-disabled={subcontratas.length === 1}
                    title={subcontratas.length === 1 ? 'Debe existir al menos una subcontrata' : 'Eliminar'}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-5 h-5"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
                      <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                      <line x1="10" y1="11" x2="10" y2="17" />
                      <line x1="14" y1="11" x2="14" y2="17" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Campos principales */}
              <div className="flex flex-col gap-4">

                <ClienteDropdownSubcontrata
                  subcontrata={sc}
                  index={idx}
                  updateSubcontrata={updateSubcontrata}
                />

                {/* <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Nombre Subcontrata*
                  </label>
                  <input
                    type="text"
                    className="inputModal w-full p-2 border rounded bg-white"
                    value={sc.nombre}
                    onChange={(e) => updateSubcontrata(idx, "nombre", e.target.value)}
                  />
                </div> */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Especialidad*
                  </label>
                  <input
                    type="text"
                    className="inputModal w-full p-2 border rounded bg-white"
                    value={sc.especialidad}
                    onChange={(e) =>
                      updateSubcontrata(idx, "especialidad", e.target.value)
                    }
                  />
                </div>
              </div>

              {/* Contacto */}
              <div className="flex flex-col gap-4">
                <p className="text-xs font-bold text-gray-500">Persona de contacto:</p>

                <hr></hr>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Nombre
                  </label>
                  <input
                    type="text"
                    className="inputModal w-full p-2 border rounded bg-white"
                    value={sc.contacto.nombre}
                    onChange={(e) =>
                      updateSubcontrata(idx, "contacto.nombre", e.target.value)
                    }
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Teléfono
                  </label>
                  <input
                    type="tel"
                    className="inputModal w-full p-2 border rounded bg-white"
                    value={sc.contacto.telefono}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (/^\d*$/.test(v))
                        updateSubcontrata(idx, "contacto.telefono", v);
                    }}
                    maxLength={15}
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    className={`inputModal w-full p-2 border rounded bg-white ${
                      subcontrataEmailErrors[idx] ? 'border-red-500 focus:ring-red-500' : ''
                    }`}
                    placeholder='nombre@dominio.com'
                    value={sc.contacto.email}
                    onChange={(e) => {
                      const v = e.target.value.replace(/^\s+/, '');
                      updateSubcontrata(idx, "contacto.email", v);
                      
                      // Validar email en tiempo real (solo si hay contenido)
                      if (v && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
                        setSubcontrataEmailErrors(prev => ({
                          ...prev,
                          [idx]: 'Email inválido'
                        }));
                      } else {
                        setSubcontrataEmailErrors(prev => {
                          const newErrors = { ...prev };
                          delete newErrors[idx];
                          return newErrors;
                        });
                      }
                    }}
                    onBlur={(e) => {
                      const v = e.target.value.trim().toLowerCase();
                      updateSubcontrata(idx, "contacto.email", v);
                      
                      // Validación más estricta en blur (solo si hay contenido)
                      if (v) {
                        const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
                        if (!emailRegex.test(v)) {
                          setSubcontrataEmailErrors(prev => ({
                            ...prev,
                            [idx]: 'Formato de email inválido. Debe incluir @ y un dominio válido (.com, .es, etc.)'
                          }));
                        } else {
                          setSubcontrataEmailErrors(prev => {
                            const newErrors = { ...prev };
                            delete newErrors[idx];
                            return newErrors;
                          });
                        }
                      } else {
                        // Si está vacío, limpiar el error (es opcional)
                        setSubcontrataEmailErrors(prev => {
                          const newErrors = { ...prev };
                          delete newErrors[idx];
                          return newErrors;
                        });
                      }
                    }}
                  />
                  {subcontrataEmailErrors[idx] && (
                    <p className="text-red-500 text-xs mt-1">{subcontrataEmailErrors[idx]}</p>
                  )}
                </div>

                <div className="flex-1 min-w-[180px]">
                  <label className="block text-xs font-medium text-gray-600 mb-1 truncate">Comentarios</label>
                  <textarea className="truncatecomentarios shadow-sm w-full p-2 border rounded bg-white mb-0 resize-y min-h-[60px] text-sm" value={obra.Comentarios || ''} onChange={e => setObra({ ...obra, Comentarios: e.target.value })} />
                </div>

              </div>
            </div>
            );
          })}

        {/* Tarjeta "Añadir" como un item del grid (centrado V/H) */}
        {subcontratas.length < 5 && (
          <div className="border-2 border-dashed rounded-lg p-4 bg-white/60 min-h-[220px] flex items-center justify-center" style={{borderColor: '#4299e1'}}>
            <button
              type="button"
              onClick={addSubcontrata}
              className="w-16 h-16 flex items-center justify-center rounded-full border border-blue-400 text-blue-600 hover:bg-blue-50"
              title="Añadir subcontrata"
              aria-label="Añadir subcontrata"
            >
              {/* Icono SVG (plus) */}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                className="w-30 h-30"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                {/* círculo opcional para look más “botón” */}
                <circle cx="12" cy="12" r="10" className="opacity-0" />
                <line x1="12" y1="7" x2="12" y2="17" />
                <line x1="7" y1="12" x2="17" y2="12" />
              </svg>
            </button>
          </div>
        )}

        </div>


        {/* </div> */}

{/* Botones de acción (estilo minimal) */}
        <div className="flex justify-end mt-2 gap-2 sm:gap-2 pt-3 mb-5 md:mb-0">
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1.5 rounded-lg text-red-600 border bg-red-50 hover:bg-red-100 border-red-200 text-sm font-medium transition-colors"
            title="Cerrar sin guardar"
          >
            Cancelar
          </button>

          {(() => {
            const hasSubcontrataEmailErrors = Object.keys(subcontrataEmailErrors).length > 0;
            const canSave = !saving && !successMsg && !errorMsg && !emailError && !hasSubcontrataEmailErrors && !isAdmin; // 🔒 Deshabilitar para admins
            const disabledReason = isAdmin
              ? 'Los administradores no pueden crear obras'
              : successMsg
              ? 'La operación ya se completó'
              : errorMsg
              ? 'Corrige los errores antes de continuar'
              : emailError
              ? 'Email del contacto inválido'
              : hasSubcontrataEmailErrors
              ? 'Emails de subcontratas inválidos'
              : saving
              ? 'Guardando…'
              : '';
            return (
              <button
                type="button"
                onClick={handleSave}
                disabled={!canSave}
                aria-busy={saving || undefined}
                title={!canSave ? disabledReason : 'Crear obra'}
                className={`px-3 py-1.5 rounded-lg text-blue-600 border bg-blue-50 hover:bg-blue-100 border-blue-200 text-sm font-medium transition-colors ${
                  canSave
                    ? 'hover:bg-blue-100 border-blue-200'
                    : 'bg-white text-gray-400 border-gray-200 cursor-not-allowed'
                }`}
              >
                {saving ? (
                  <>
                    <svg className="h-3 w-3 inline-block mr-2 animate-spin align-[-2px]" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                    </svg>
                    Guardando…
                  </>
                ) : (
                  'Guardar'
                )}
              </button>
            );
          })()}
        </div>
        {/* Overlay de Success */}
        {successMsg && (
          <div className="fixed inset-0 flex items-center justify-center" style={{zIndex: 1500}}>
            <div className="bg-gray-600 rounded-2xl px-6 py-5 shadow-2xl w-auto max-w-md">
              <div className="flex flex-col items-center text-center gap-3">
                <CircleCheck className="w-20 h-20 text-green-500" aria-hidden="true" />
                <p role="alert" aria-live="polite" className="text-white text-base font-medium px-2">
                  {successMsg}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Overlay de Error */}
        {errorMsg && (
          <div 
            className="fixed inset-0 flex items-center justify-center cursor-pointer"
            style={{zIndex: 1500}}
            onClick={() => setErrorMsg('')}
            title="Haz clic para cerrar"
          >
            <div className="bg-gray-600 rounded-2xl px-6 py-5 shadow-2xl w-auto max-w-md">
              <div className="flex flex-col items-center text-center gap-3">
                <CircleX className="w-20 h-20 text-red-500" aria-hidden="true" />
                {errorMsg.includes('TITULO||') ? (
                  <div role="alert" aria-live="polite" className="text-white px-2 text-left w-full">
                    {(() => {
                      const parts = errorMsg.split('||');
                      const titulo = parts[1] || '';
                      const lista = parts[3] || '';
                      return (
                        <>
                          <p className="text-base font-medium mb-2">{titulo}</p>
                          <p className="text-sm whitespace-pre-line">{lista}</p>
                        </>
                      );
                    })()}
                  </div>
                ) : (
                  <p role="alert" aria-live="polite" className="text-white text-base font-medium px-2">
                    {errorMsg}
                  </p>
                )}
                <button
                  type="button"
                  className="mt-1 text-xs px-3 py-1 rounded-md bg-gray-600 text-white hover:bg-gray-500"
                  onClick={() => setErrorMsg("")}
                >
                  Cerrar
                </button>
              </div>
            </div>

          </div>
        )}

      </div>

      </div>
    </div>
  );
};

export default ObraNueva;
